import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../shared/utils/mobile';
import { Column } from 'primereact/column';
import type { AdsAdvertiserItem, AdsCreativeItem } from '@brand24-original/llm-types';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { SearchFilter } from '../../../shared/components/search-filter/SearchFilter';
import { AdsFilters } from '../../../shared/api/services/ads/AdsService';
import { useGetAdsCreatives } from '../../../shared/api/hooks/ads/useGetAdsCreatives';
import { useGetAdsCreativePrompts } from '../../../shared/api/hooks/ads/useGetAdsCreativePrompts';
import AdsExportButton from './AdsExportButton';
import {
  AdCopyCell,
  AdsEmpty,
  AdsErrorPanel,
  AdvertiserCell,
  DomainLink,
  ModelBadges,
  PAGINATOR_TEMPLATE,
  TableSkeleton,
  TargetUrlLink,
  useDayFormatter,
  ViewSwitch,
} from './AdsTableParts';

type BreakdownTab = 'advertisers' | 'ads';

/** By advertiser / By ad — a `.switch-box` in the title row, next to search + export. */
const TABS: { value: BreakdownTab; labelKey: string; icon: string }[] = [
  { value: 'advertisers', labelKey: 'ads.tabByAdvertiser', icon: 'ico--globe' },
  { value: 'ads', labelKey: 'ads.tabByAd', icon: 'ico--ads' },
];

interface AdsBreakdownSectionProps {
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  filters: AdsFilters;
  advertisers: AdsAdvertiserItem[];
  isAdvertisersLoading: boolean;
  isAdvertisersError: boolean;
  onRetryAdvertisers: () => void;
}

/** By-advertiser row expansion: the advertiser's creatives (fetched on expand). */
const AdsAdvertiserExpansion: React.FC<{
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  filters: AdsFilters;
  domain: string;
}> = ({ projectId, startDate, endDate, filters, domain }) => {
  const { t } = useTranslation();
  const { creatives, isLoading, isError, refetch } = useGetAdsCreatives(projectId, startDate, endDate, {
    ...filters,
    advertisers: [domain],
  });

  if (isLoading) {
    return <TableSkeleton compact />;
  }
  if (isError) {
    return <AdsErrorPanel framed onRetry={() => void refetch()} />;
  }

  return (
    <DataTable
      stripedRows
      value={creatives}
      dataKey="adKey"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
      tableStyle={{ tableLayout: 'fixed' }}
    >
      <Column
        header={t('ads.colAdCreative')}
        headerClassName="table__header"
        bodyClassName="table__cell table__cell--stack-title"
        style={{ minWidth: '280px', width: 'auto' }}
        body={(row: AdsCreativeItem) => (
          <AdCopyCell
            title={row.title}
            body={row.body}
            imageUrl={row.imageUrl}
            faviconUrl={row.faviconUrl}
            domain={row.domain}
            advertiser={row.advertiser}
            targetUrl={row.targetUrl}
          />
        )}
      />
      <Column
        header={t('ads.colSendsTo')}
        headerClassName="table__header"
        bodyClassName="table__cell ads-page__link-cell table__cell--stack-wide"
        style={{ width: '340px' }}
        body={(row: AdsCreativeItem) => (row.targetUrl ? <TargetUrlLink targetUrl={row.targetUrl} /> : '-')}
      />
      <Column
        field="prompts"
        header={t('ads.colPrompts')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell table__cell--center"
        style={{ width: '90px' }}
      />
      <Column
        field="occurrences"
        header={t('ads.colTimesSeen')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell table__cell--center"
        style={{ width: '110px' }}
      />
    </DataTable>
  );
};

/** By-ad row expansion: prompts the creative appeared at (fetched on expand). */
const AdsCreativeExpansion: React.FC<{
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  filters: AdsFilters;
  adKey: string;
}> = ({ projectId, startDate, endDate, filters, adKey }) => {
  const { t } = useTranslation();
  const formatDay = useDayFormatter();
  const { creativePrompts, isLoading, isError, refetch } = useGetAdsCreativePrompts(
    projectId,
    startDate,
    endDate,
    adKey,
    filters
  );

  if (isLoading) {
    return <TableSkeleton compact />;
  }
  if (isError) {
    return <AdsErrorPanel framed onRetry={() => void refetch()} />;
  }

  return (
    <DataTable
      stripedRows
      value={creativePrompts}
      dataKey="monitorId"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
      tableStyle={{ tableLayout: 'fixed' }}
    >
      <Column
        field="prompt"
        header={t('ads.colPrompt')}
        headerClassName="table__header"
        bodyClassName="table__cell table__cell--stack-title"
      />
      <Column
        field="occurrences"
        header={t('ads.colTimesSeen')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell table__cell--center"
        style={{ width: '110px' }}
      />
      <Column
        header={t('ads.colLatest')}
        headerClassName="table__header table__header--right"
        bodyClassName="table__cell table__cell--right"
        style={{ width: '110px' }}
        body={(row) => formatDay(row.latest)}
      />
    </DataTable>
  );
};

/** By advertiser / By ad breakdown — one panel, switch-box, search + CSV export per active view. */
const AdsBreakdownSection: React.FC<AdsBreakdownSectionProps> = ({
  projectId,
  startDate,
  endDate,
  filters,
  advertisers,
  isAdvertisersLoading,
  isAdvertisersError,
  onRetryAdvertisers,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const formatDay = useDayFormatter();
  const [activeTab, setActiveTab] = useState<BreakdownTab>('advertisers');
  const [search, setSearch] = useState('');
  // `any` matches the app-wide DataTable expansion pattern (see DomainsWithSourcesStatsTable).
  const [expandedAdvertisers, setExpandedAdvertisers] = useState<any>(null);
  const [expandedCreatives, setExpandedCreatives] = useState<any>(null);

  const {
    creatives,
    isLoading: isCreativesLoading,
    isError: isCreativesError,
    refetch: refetchCreatives,
  } = useGetAdsCreatives(projectId, startDate, endDate, filters, activeTab === 'ads');

  const normalizedSearch = search.trim().toLowerCase();
  const matches = (...values: (string | null | undefined)[]): boolean =>
    !normalizedSearch || values.some((value) => value?.toLowerCase().includes(normalizedSearch));

  const visibleAdvertisers = useMemo(
    () =>
      advertisers.filter(
        (row) =>
          (!filters.advertisers?.length || filters.advertisers.includes(row.domain)) &&
          matches(row.advertiser, row.domain)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [advertisers, filters.advertisers, normalizedSearch]
  );
  const visibleCreatives = useMemo(
    () => creatives.filter((row) => matches(row.title, row.body, row.advertiser, row.domain)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatives, normalizedSearch]
  );

  // Null dates = FiltersContext still hydrating (the disabled query reports isLoading=false) — the
  // page ORs that window into isAdvertisersLoading, but the creatives tab fetches here, so it needs
  // the same guard or a project switch with this tab active flashes the empty table.
  const isCreativesTabLoading = isCreativesLoading || !startDate || !endDate;
  const isActiveTabLoading =
    activeTab === 'advertisers' ? isAdvertisersLoading : isCreativesTabLoading;
  const isActiveTabError = activeTab === 'advertisers' ? isAdvertisersError : isCreativesError;
  const retryActiveTab = activeTab === 'advertisers' ? onRetryAdvertisers : () => void refetchCreatives();
  const activeTabRowCount = activeTab === 'advertisers' ? advertisers.length : creatives.length;
  // No ads in range at all (the advertisers list is not advertiser-filtered) → the switch and the
  // search/export tools would float over an empty panel — hide them.
  const showTools = isAdvertisersLoading || advertisers.length > 0;

  return (
    <div className="stats__panel ads-page__breakdown">
      <div className="page-box__title-action">
        {/* "What am I looking at" next to the title, tools (search, export) on the right edge. */}
        <div className="page-box__title page-box__title--flex">
          {t('ads.breakdownTitle')}
          <CustomTooltip tooltipText="ads.breakdownTooltip" addMargin={false} />
          {showTools && <ViewSwitch options={TABS} value={activeTab} onChange={setActiveTab} />}
        </div>
        {showTools && (
          <div className="page-box__source-filter">
            <SearchFilter
              value={search}
              onChange={setSearch}
              placeholder={t('ads.searchPlaceholder')}
              className="search-filter--inline"
            />
            <AdsExportButton
              id="btn-export-ads"
              projectId={projectId}
              view={activeTab === 'advertisers' ? 'brands' : 'items'}
              startDate={startDate}
              endDate={endDate}
              filters={filters}
              hasRows={activeTabRowCount > 0}
            />
          </div>
        )}
      </div>

      {isActiveTabLoading ? (
        <TableSkeleton />
      ) : isActiveTabError ? (
        <AdsErrorPanel framed onRetry={retryActiveTab} />
      ) : activeTab === 'advertisers' ? (
        <DataTable
          paginator
          paginatorClassName="pagination"
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          removableSort
          tableStyle={{ width: '100%' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--accordion table-wrapper--stack"
          responsiveLayout="stack"
          emptyMessage={() => <AdsEmpty />}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate={isMobile ? MOBILE_PAGINATOR_TEMPLATE : PAGINATOR_TEMPLATE}
          pageLinkSize={isMobile ? 3 : 5}
          value={visibleAdvertisers}
          dataKey="domain"
          expandedRows={expandedAdvertisers}
          onRowToggle={(e) => setExpandedAdvertisers(e.data)}
          rowExpansionTemplate={(row: AdsAdvertiserItem) => (
            <AdsAdvertiserExpansion
              projectId={projectId}
              startDate={startDate}
              endDate={endDate}
              filters={filters}
              domain={row.domain}
            />
          )}
        >
          <Column
            field="advertiser"
            sortable
            header={t('ads.colAdvertiser')}
            headerClassName="table__header"
            bodyClassName="table__cell table__cell--stack-title"
            body={(row: AdsAdvertiserItem) => (
              <AdvertiserCell
                advertiser={row.advertiser}
                domain={row.domain}
                faviconUrl={row.faviconUrl}
                isYou={row.isYou}
              />
            )}
          />
          <Column
            field="domain"
            sortable
            header={t('ads.colAdvertiserDomain')}
            headerClassName="table__header"
            bodyClassName="table__cell ads-page__link-cell table__cell--stack-wide"
            style={{ width: '260px' }}
            body={(row: AdsAdvertiserItem) => <DomainLink domain={row.domain} />}
          />
          <Column
            field="prompts"
            sortable
            header={t('ads.colPrompts')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '110px' }}
          />
          <Column
            field="occurrences"
            sortable
            header={t('ads.colSeen')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '90px' }}
          />
          <Column
            field="firstSeen"
            sortable
            header={t('ads.colFirstSeen')}
            headerClassName="table__header table__header--right"
            bodyClassName="table__cell table__cell--right"
            style={{ width: '140px' }}
            body={(row: AdsAdvertiserItem) => formatDay(row.firstSeen)}
          />
          <Column
            expander={true}
            bodyClassName="table__cell--stack-actions"
            style={{ width: '25px' }}
            frozen={!isMobile}
            alignFrozen="right"
          />
        </DataTable>
      ) : (
        <DataTable
          paginator
          paginatorClassName="pagination"
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          removableSort
          tableStyle={{ width: '100%' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--accordion table-wrapper--stack"
          responsiveLayout="stack"
          emptyMessage={() => <AdsEmpty />}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate={isMobile ? MOBILE_PAGINATOR_TEMPLATE : PAGINATOR_TEMPLATE}
          pageLinkSize={isMobile ? 3 : 5}
          value={visibleCreatives}
          dataKey="adKey"
          expandedRows={expandedCreatives}
          onRowToggle={(e) => setExpandedCreatives(e.data)}
          rowExpansionTemplate={(row: AdsCreativeItem) => (
            <AdsCreativeExpansion
              projectId={projectId}
              startDate={startDate}
              endDate={endDate}
              filters={filters}
              adKey={row.adKey}
            />
          )}
        >
          <Column
            field="title"
            sortable
            header={t('ads.colAdCopy')}
            headerClassName="table__header"
            bodyClassName="table__cell table__cell--stack-title"
            style={{ minWidth: '280px', width: 'auto' }}
            body={(row: AdsCreativeItem) => (
              <AdCopyCell
                title={row.title}
                body={row.body}
                imageUrl={row.imageUrl}
                faviconUrl={row.faviconUrl}
                domain={row.domain}
                advertiser={row.advertiser}
                targetUrl={row.targetUrl}
              />
            )}
          />
          <Column
            header={t('ads.colModel')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '110px' }}
            body={(row: AdsCreativeItem) => <ModelBadges types={row.types} />}
          />
          <Column
            field="advertiser"
            sortable
            header={t('ads.colAdvertiser')}
            headerClassName="table__header"
            bodyClassName="table__cell"
            style={{ width: '130px' }}
          />
          <Column
            field="domain"
            sortable
            header={t('ads.colAdvertiserDomain')}
            headerClassName="table__header"
            bodyClassName="table__cell ads-page__link-cell table__cell--stack-wide"
            style={{ width: '170px' }}
            body={(row: AdsCreativeItem) => <DomainLink domain={row.domain} />}
          />
          <Column
            field="prompts"
            sortable
            header={t('ads.colPrompts')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '90px' }}
          />
          <Column
            field="occurrences"
            sortable
            header={t('ads.colSeen')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '70px' }}
          />
          <Column
            field="firstSeen"
            sortable
            header={t('ads.colFirstSeen')}
            headerClassName="table__header table__header--right"
            bodyClassName="table__cell table__cell--right"
            style={{ width: '120px' }}
            body={(row: AdsCreativeItem) => formatDay(row.firstSeen)}
          />
          <Column
            expander={true}
            bodyClassName="table__cell--stack-actions"
            style={{ width: '25px' }}
            frozen={!isMobile}
            alignFrozen="right"
          />
        </DataTable>
      )}
    </div>
  );
};

export default AdsBreakdownSection;
